// geoCurrency — best-effort visitor-currency detection for pricing surfaces.
//
// Mirrors the CountrySelect prefill pattern (GET /api/public/geo → Cloudflare
// IP country) but caches the promise at module level so the whole page fires
// AT MOST ONE geo fetch no matter how many components ask (pricingTiers block,
// CheckoutPage, etc.). Silent failure by design — callers stay on their base
// currency when geo is unavailable.
//
// Per PHASE2-CONTRACTS.md §5: US→USD, CA→CAD, AU→AUD, NZ→AUD, everything
// else → USD. That country mapping only ever PROPOSES a currency — an event
// shows it only when the event actually sells in it (resolveEventCurrency
// below). The AU Summit sells in AUD alone, so every visitor sees AUD there.

const API_BASE = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

/** Contract-locked default country→currency map. Blocks may override via
 *  their own `currencyByCountry` config. */
export const DEFAULT_CURRENCY_BY_COUNTRY: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'AUD',
};

let geoCountryPromise: Promise<string | null> | null = null;

/** Resolve the visitor's ISO-2 country from the engine geo endpoint.
 *  One fetch per page load (module-level cache); resolves null on any failure. */
export function fetchGeoCountry(): Promise<string | null> {
  if (!geoCountryPromise) {
    geoCountryPromise = fetch(`${API_BASE}/api/public/geo`)
      .then((r) => r.json())
      .then((d: { country?: string | null }) => {
        const cc = (d?.country ?? '').toUpperCase();
        return cc || null;
      })
      .catch(() => null);
  }
  return geoCountryPromise;
}

/** Map a country to a display currency using the given (or default) map. */
export function currencyForCountry(
  country: string | null,
  map?: Record<string, string> | null,
  fallback?: string,
): string | null {
  if (!country) return fallback ?? null;
  const m = map && Object.keys(map).length > 0 ? map : DEFAULT_CURRENCY_BY_COUNTRY;
  return m[country.toUpperCase()] ?? fallback ?? null;
}

/** The currency a visitor sees and pays in for ONE event — the single rule
 *  shared by the pricingTiers block and CheckoutPage, so the price on the card
 *  and the charge at checkout can never disagree.
 *
 *  An event sells in a fixed set of currencies (`offered` — the keys of its
 *  tiers' pricing / checkoutUrls maps). Each currency is its own Spiffy
 *  account and its own selling entity (AUD = PB Australia, USD/CAD = TSB
 *  Global), so a currency the event does not sell in is never shown or
 *  charged, whatever the visitor's country or a hand-edited `?cur=`:
 *    1. The event sells in exactly one currency → that currency, instantly
 *       (no geo wait). This is the AU Summit: AUD for every visitor.
 *    2. `requested` (`?cur=` from a pricing-card CTA) when the event sells in it.
 *    3. Geo still in flight (`country === undefined`) → null; caller waits.
 *    4. The visitor's geo currency when the event sells in it.
 *    5. Otherwise the event's own `base` currency.
 *  An event with no `offered` currencies (a legacy single-checkout intent)
 *  keeps the plain behaviour: `requested`, else geo → USD.
 *  No manual switcher (Michael, 2026-09-16) — this decides, the visitor never picks. */
export function resolveEventCurrency(opts: {
  requested?: string | null;
  /** ISO-2 visitor country; null = unknown; undefined = geo still in flight. */
  country: string | null | undefined;
  offered: string[];
  base: string;
  map?: Record<string, string> | null;
}): string | null {
  const offered = opts.offered.map((c) => c.toUpperCase());
  const requested = (opts.requested ?? '').trim().toUpperCase() || null;
  if (offered.length === 0) {
    if (requested) return requested;
    if (opts.country === undefined) return null;
    return currencyForCountry(opts.country, opts.map, 'USD');
  }
  if (offered.length === 1) return offered[0];
  if (requested && offered.includes(requested)) return requested;
  if (opts.country === undefined) return null;
  const geo = currencyForCountry(opts.country, opts.map, 'USD');
  if (geo && offered.includes(geo)) return geo;
  const base = opts.base.toUpperCase();
  return offered.includes(base) ? base : offered[0];
}

/** Currencies a checkout can actually charge in — the per-currency
 *  checkoutUrls entries that carry a URL. */
export function offeredCheckoutCurrencies(checkoutUrls: Record<string, string> | null | undefined): string[] {
  return Object.entries(checkoutUrls ?? {}).filter(([, url]) => !!url).map(([c]) => c.toUpperCase());
}

/** A checkout's own base currency: the checkoutUrls key whose URL is the
 *  buyer-intent's own `spiffy_checkout_url` (the generator points that at the
 *  event's baseCurrency account), else USD. */
export function checkoutBaseCurrency(
  checkoutUrls: Record<string, string> | null | undefined,
  spiffyCheckoutUrl: string,
): string {
  const hit = Object.entries(checkoutUrls ?? {}).find(([, url]) => !!url && url === spiffyCheckoutUrl);
  return hit ? hit[0].toUpperCase() : 'USD';
}
